import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartCtx = createContext(null);
const KEY = "cl_cart_v1";

const loadInitial = () => {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(KEY) : null;
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  // Lazy initializer runs exactly once — avoids the StrictMode double-effect race
  // that previously wiped the cart on every full page load.
  const [items, setItems] = useState(loadInitial);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (err) {
      console.warn("Failed to persist cart:", err?.message);
    }
  }, [items]);

  const add = useCallback((product, qty = 1) => {
    setItems((cur) => {
      const found = cur.find((c) => c.id === product.id);
      if (found) {
        return cur.map((c) => (c.id === product.id ? { ...c, quantity: c.quantity + qty } : c));
      }
      return [...cur, { id: product.id, title: product.title, price: product.price,
                        image: (product.images && product.images[0]) || null, quantity: qty }];
    });
    toast.success(`${product.title} added to cart`);
  }, []);

  const remove = useCallback((id) => setItems((cur) => cur.filter((c) => c.id !== id)), []);
  const setQty = useCallback((id, q) => setItems((cur) => cur.map((c) => (c.id === id ? { ...c, quantity: Math.max(1, q) } : c))), []);
  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((a, c) => a + c.quantity, 0);
  const subtotal = items.reduce((a, c) => a + c.price * c.quantity, 0);

  const value = useMemo(
    () => ({ items, add, remove, setQty, clear, count, subtotal }),
    [items, add, remove, setQty, clear, count, subtotal]
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
